/**
 * TimepointProblemModal — 추이 차트 시점 클릭 → 그 시점 시험의 문제를 정오와 함께 '쭉 이어서' 미리보기.
 *
 * ProblemPreviewPanel과 동일하게 단일 카드 리스트(본문+정오+개념배지)를 연속으로 렌더한다.
 *
 * ══ 미리보기가 두 벌인 이유 — 이 창은 **세 화면이 공유**한다 ═══════════════════
 * 부모 사슬이 ConceptTrendModal → AITrackerContent 이고, 그 부품을 학습(/students-hub, 대상)과
 * KyTestPage·StudentManagement(대상 밖)가 함께 쓴다. v2 로 갈아끼우는 범위는 **네 허브 메뉴뿐**
 * 이라, 여기서 통째로 바꾸면 지시 범위 밖 두 화면이 같이 바뀐다.
 * 그래서 파일을 복제(포크)하지 않고 `useV2Preview` 로 가른다 — 켜는 것은 학습(/students-hub)뿐이고
 * 기본값은 꺼짐이다. (같은 파일의 hideEngineSummary 가 쓰는 방식이고, KyTestPage:366 이
 *  「공유 컴포넌트를 손대지 않고 플래그로 이 페이지만」이라 적어 둔 그 선례다.)
 * 포크하지 않는 이유는 이 저장소가 이미 앓고 있는 **유물 병존**을 하나 더 늘리지 않기 위해서다.
 * ══════════════════════════════════════════════════════════════════════════
 * 데이터=useTimepointExamProblems. 기본은 기존 exams API이고, 학습 v2만 includeV2Assignments로
 * 마감된 신 시험지 배정 결과를 같은 KST 날짜에 합친다.
 * 진입 분기: 그래프에서 개념을 '선택'(범례 강조·단일 라인)하고 클릭했으면 conceptCid로 그 개념 문제만
 * 필터(틀린문제 우선)한 상태로 시작하고, 선택 없이 클릭했으면(conceptCid=null) 전체 문제를 원순서로 보여준다.
 * 요약줄의 개념 칩으로 필터를 켜고 끌 수 있으며, 헤더는 개념 유무에 따라 '추적'/'전체 문제'로 바뀐다.
 */
import { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import MathName from './SyllabusV2/MathName';
import { Check } from 'lucide-react';
import { Modal } from './common';
import ProblemPreviewCards from './ProblemPreviewCards';
import ProblemCardsV2 from './ExamPapersV2/ProblemCardsV2';
import useTimepointExamProblems from '../utils/useTimepointExamProblems';

const ProblemPreview = lazy(() => import('./ProblemPreview'));
const ProblemPreviewV2 = lazy(() => import('./ExamPapersV2/ProblemPreviewV2'));

function StateBox({ children }) {
  return (
    <div className="flex h-full items-center justify-center px-3 text-center text-sm text-gray-400">
      {children}
    </div>
  );
}

function TimepointProblemModal({ isOpen, onClose, studentId, date, conceptCid, conceptName, useV2Preview = false, includeV2Assignments = false }) {
  // 카드형은 받는 props 가 두 벌 모두 같아 부품만 갈아 끼운다(ProblemCardsV2 가 구형 시그니처를
  // 그대로 받도록 만들어져 있다). 창은 prop 이름이 하나 다르다 — problemNumbers → numbers.
  const Cards = useV2Preview ? ProblemCardsV2 : ProblemPreviewCards;
  const { load, isLoading, error } = useTimepointExamProblems(studentId, { includeV2Assignments });
  const [data, setData] = useState(null);
  const [previewState, setPreviewState] = useState({ ids: [], correct: [null], number: [1] });
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [wrongOnly, setWrongOnly] = useState(false); // 틀린 문제만(result===false) 보기
  const [conceptFilter, setConceptFilter] = useState(false); // 개념(C) 문제만 보기(틀린문제 우선)

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setData(null);
    setIsPreviewOpen(false);
    setWrongOnly(false);
    setConceptFilter(false);
    load(date, conceptCid).then((d) => {
      if (cancelled) return;
      setData(d);
      // 그래프에서 개념 선택 후 진입 + 그 개념 문제가 실제로 있으면 → 개념 필터 ON(틀린문제 우선)로 시작
      setConceptFilter(conceptCid != null && (d?.conceptC?.length || 0) > 0);
    });
    return () => { cancelled = true; };
  }, [isOpen, date, conceptCid, load]);

  const hasConcept = conceptCid != null && conceptCid !== '';
  const conceptC = data?.conceptC || [];
  const allProblems = data?.problems || [];

  // 틀린 문제(result===false)를 앞으로 당기는 안정 정렬(그룹 내 원순서 유지)
  const wrongFirst = (arr) => [
    ...arr.filter((p) => p.result === false),
    ...arr.filter((p) => p.result !== false),
  ];

  // 표시 리스트: 개념 필터 ON → 그 개념 문제만(틀린문제 우선), OFF/개념 없음 → 전체 원순서
  const listBase = hasConcept && conceptFilter ? wrongFirst(conceptC) : allProblems;
  const visibleProblems = wrongOnly ? listBase.filter((p) => p.result === false) : listBase;

  const getProblemResult = useCallback((_test, pid) => {
    const rec = data?.problems?.find((r) => String(r.problem_id) === String(pid));
    return rec ? rec.result : null;
  }, [data]);

  const handleOpenProblemPreview = useCallback((problemIds, index, isCorrect = null) => {
    const ids = Array.isArray(problemIds) ? problemIds : [problemIds];
    setPreviewState({ ids, correct: isCorrect !== null ? [isCorrect] : [null], number: [index + 1] });
    setIsPreviewOpen(true);
  }, []);

  const totals = data?.totals;

  let listArea;
  if (isLoading || (!data && !error)) {
    listArea = (
      <StateBox>
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </StateBox>
    );
  } else if (error) {
    listArea = <StateBox>{error}</StateBox>;
  } else if (!data?.matched) {
    listArea = <StateBox>이 시점({date})에 해당하는 시험 기록을 찾지 못했습니다.</StateBox>;
  } else if (allProblems.length === 0) {
    listArea = <StateBox>이 시험의 문제 기록이 없습니다.</StateBox>;
  } else if (visibleProblems.length === 0) {
    listArea = <StateBox>{wrongOnly ? '틀린 문제가 없습니다.' : '해당하는 문제가 없습니다.'}</StateBox>;
  } else {
    listArea = (
      <Cards
        key={`${date}-${conceptCid}-${conceptFilter ? 'c' : 'x'}-${wrongOnly ? 'w' : 'a'}`}
        problems={visibleProblems}
        selectedTest={null}
        onViewProblem={handleOpenProblemPreview}
        highlightedIndex={null}
        onHighlight={() => {}}
        getProblemResult={getProblemResult}
        isDesktop
        showConcepts
      />
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={hasConcept ? (
        // 날짜/'시험'은 기본 크기 유지, 매스컨셉 관련 부분('X' 추적)만 축소 → 개념명이 길어도 헤더 개행 방지
        <span>
          {`${date || ''} 시험 · `}
          <span className="text-sm">&lsquo;<MathName text={conceptName} compact />&rsquo; 추적</span>
        </span>
      ) : (
        `${date || ''} 시험 · 전체 문제`
      )}
      size="md"
      className="!w-[95vw] !max-w-none md:!w-[480px]"
      fixedHeight
      maxHeight="85vh"
      bodyClassName="min-h-0 overflow-hidden"
      zIndex={9999950}
    >
      <div className="flex h-full min-h-0 flex-col gap-2">
        {data?.matched && totals && totals.n > 0 && (
          <div className="flex shrink-0 flex-wrap items-center gap-1 px-1 text-xs text-gray-500">
            <span>
              총 <span className="font-semibold text-gray-700">{totals.n}</span>문항 · 정답{' '}
              <span className="font-semibold text-primary">{totals.correct}</span> · 정답률{' '}
              {Math.round((totals.correct / totals.n) * 100)}%
            </span>
            {/* 매스컨셉 필터 상태 — 항상 한 칸 표시(미선택/토글/해당없음) */}
            {(() => {
              if (!hasConcept) {
                return (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-400"
                    title="그래프에서 개념을 선택하지 않아 전체 문제를 표시합니다"
                  >
                    매스컨셉 미선택 · 전체
                  </span>
                );
              }
              if (conceptC.length > 0) {
                return (
                  <button
                    type="button"
                    onClick={() => setConceptFilter((v) => !v)}
                    aria-pressed={conceptFilter}
                    title={conceptFilter ? '개념 필터 해제 — 전체 문제 보기' : `'${conceptName}' 개념 문제만 보기`}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                      conceptFilter
                        ? 'border-primary/40 bg-primary/10 text-primary'
                        : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {conceptFilter && <Check className="h-3 w-3" />}
                    개념 &lsquo;<MathName text={conceptName} compact />&rsquo; {conceptC.length}
                  </button>
                );
              }
              return (
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[11px] font-medium text-gray-400"
                  title={`'${conceptName}' 개념에 해당하는 문항이 이 시험에 없어 전체를 표시합니다`}
                >
                  개념 &lsquo;<MathName text={conceptName} compact />&rsquo; · 해당 문항 없음
                </span>
              );
            })()}
            {/* 틀린 문제만 토글 — 헤더에서 이동. ON/OFF를 명시적으로 표기 */}
            <button
              type="button"
              onClick={() => setWrongOnly((v) => !v)}
              aria-pressed={wrongOnly}
              title="틀린 문제만 보기 토글"
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                wrongOnly ? 'border-danger/40 bg-danger/10 text-danger' : 'border-gray-300 text-gray-500 hover:bg-gray-50'
              }`}
            >
              틀린 문제만
              <span
                className={`rounded px-1 text-[10px] font-bold leading-none ${
                  wrongOnly ? 'bg-danger text-white' : 'bg-gray-400 text-white'
                }`}
              >
                {wrongOnly ? 'ON' : 'OFF'}
              </span>
            </button>
            {wrongOnly && <span className="font-medium text-danger">{visibleProblems.length}문항</span>}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-hidden">{listArea}</div>
      </div>

      {isPreviewOpen && (
        <Suspense fallback={null}>
          {useV2Preview ? (
            <ProblemPreviewV2
              isOpen={isPreviewOpen}
              onClose={() => setIsPreviewOpen(false)}
              problemIds={previewState.ids}
              showNumbers
              correctList={previewState.correct}
              numbers={previewState.number}
            />
          ) : (
            <ProblemPreview
              isOpen={isPreviewOpen}
              onClose={() => setIsPreviewOpen(false)}
              problemIds={previewState.ids}
              showNumbers
              correctList={previewState.correct}
              problemNumbers={previewState.number}
            />
          )}
        </Suspense>
      )}
    </Modal>
  );
}

export default TimepointProblemModal;
